import {DataPoint} from './core.js';
import {getLogger} from '../logger.js';
import {objectAsKey} from '../common/utilities.js';
import {IOGateway} from '../../gateway';

const log = getLogger('gateway.metrics.common');

export type MetricsEvent = StatusUpdateEvent | DataPointEvent;

export type StatusUpdateEvent = {
    identity: IOGateway.Metrics.Identity
    status: IOGateway.Metrics.Status
    metadata?: Record<string, unknown>
}

export type DataPointEvent = {
    identity: IOGateway.Metrics.Identity,
    metric: IOGateway.Metrics.Definition,
    datapoint: Omit<DataPoint, 'name'>
}

function extractMetadata(metadata?: Record<string, unknown>) {
    if (metadata && Object.keys(metadata).length > 0) {
        return metadata;
    }
    return undefined;
}

function value(v: string | number | object): IOGateway.Metrics.Value {
    if (typeof v === 'object') {
        const composite = Object.entries(v).reduce((acc, [key, val]) => {
            acc[key] = value(val);
            return acc;
        }, {});
        return {value: composite};
    } else if (typeof v === 'string') {
        return {value: v};
    } else {
        return {value: v};
    }
}

function datapoint(dp: Omit<DataPoint, 'name'>): IOGateway.Metrics.DataPoint {
    return {timestamp: dp.timestamp ?? Date.now(), value: value(dp.value)};
}


function groupBy<T>(data: Iterable<T>, keyFn: (t: T) => string): Iterable<[string, T[]]> {
    return Array.from(data).reduce((acc, t) => {
        const key = keyFn(t);
        const values = acc.get(key) ?? [];
        if (values.length === 0) {
            acc.set(key, values);
        }
        values.push(t);
        return acc;
    }, new Map<string, T[]>());
}

/**
 * Conflates a vector of metric updates based on their repository's identity.
 */
export function* conflateRepo(maxCount: number, data: MetricsEvent[]): Generator<IOGateway.Metrics.Update> {
    if (log.enabledFor('trace')) {
        log.debug(`conflating ${JSON.stringify(data)}`);
    }
    const dataByIdentity = groupBy(data, (update) => objectAsKey(update.identity));

    let current: IOGateway.Metrics.Update | undefined = undefined;
    for (const [, dps] of dataByIdentity) {
        // need to port partition-all?
        for (const e of dps) {
            if (e['status']) {
                const {identity, status, metadata} = e as StatusUpdateEvent;
                // this is a status event
                // emit the current + status message and clear current
                if (current) {
                    yield current;
                }
                yield {identity, status, metrics: undefined, metadata: extractMetadata(metadata)};
                current = undefined;
            } else {

                // this is a data point
                const {metric: definition, identity, datapoint: dp} = (e as DataPointEvent);
                const name = definition?.name;
                if (!current) {
                    current = {identity};
                }
                current.metrics = current.metrics ?? {};
                current.metrics[name] = current.metrics[name] ?? {definition: {...definition}, datapoints: []};
                delete (current.metrics[name].definition as { name?: string })['name'];

                current.metrics[name].datapoints.push(datapoint(dp));
            }
        }
        if (current) {
            yield current;
        }
    }
}
